import React, { useState, useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import { styled, useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useLocation } from 'react-router-dom';
import menuItems from './SideBarItems';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export const SidebarWidth = `320px`;
export let isSideBarOpen = true;

const StyledListItemButton = styled(ListItemButton)(({ theme, selected, isSubmenu }) => ({
  minHeight: 52,
  borderRadius: 14,
  margin: '6px 6px 6px 6px',
  padding: '14px 20px',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  '&::before': selected ? {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 5,
    height: '70%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '0 6px 6px 0',
  } : {},
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'translateX(6px)',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
      transform: 'scale(1.15)',
    },
  },
  ...(selected && {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
    fontWeight: 600,
            boxShadow: `0px 2px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  }),
  ...(isSubmenu && {
    marginLeft: '16px',
    paddingLeft: '16px',
    minHeight: 48,
    '&::before': selected ? {
      left: -8,
      width: 4,
    } : {},
  }),
}));

const Item = ({
  title,
  to,
  icon,
  selected,
  setSelected,
  submenuItems,
  open,
  logout,
  isSubmenu = false,
}) => {
  const location = useLocation();
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [showText, setShowText] = useState(false);
  const isActive = !isSubmenu && location.pathname === to;
  const hasActiveSubmenu = submenuItems?.some(item => location.pathname === item.to);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setShowText(true);
      }, 110);
      return () => clearTimeout(timer);
    } else {
      setShowText(false);
    }
  }, [open]);

  useEffect(() => {
    if (hasActiveSubmenu && !subMenuOpen) {
      setSubMenuOpen(true);
    }
  }, [hasActiveSubmenu, subMenuOpen]);

  const handleClick = () => {
    if (submenuItems) {
      setSubMenuOpen(!subMenuOpen);
    } else {
      setSelected(title);
    }
  };

  const isSelected = isActive || selected === title;

  return (
    <>
      <ListItem disablePadding>
        <StyledListItemButton
          selected={isSelected}
          isSubmenu={isSubmenu}
          onClick={logout ? logout : handleClick}
          {...(!submenuItems && { component: Link, to })}
          sx={{
            justifyContent: open ? "flex-start" : "center",
            width: "100%",
          }}
        >
          <ListItemIcon
            aria-label="menu item icon"
            sx={{
              minWidth: open ? 48 : 56,
              mr: open ? 2.5 : 0,
              justifyContent: "center",
              display: "flex",
              transition: 'all 0.2s ease-in-out',
              color: isSelected ? 'inherit' : 'inherit',
              '& svg': {
                fontSize: '1.5rem',
              },
            }}
          >
            {icon}
          </ListItemIcon>

          {open && showText && (
            <ListItemText
              primary={title}
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
                margin: 0,
                padding: 0,
                "& .MuiTypography-root": {
                  lineHeight: 1.5,
                  fontSize: '1rem',
                  letterSpacing: '0.01em',
                },
              }}
            />
          )}

          {submenuItems && open && showText && (
            <Box
              sx={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 0.3s ease',
                transform: subMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 22, ml: 1 }} />
            </Box>
          )}
        </StyledListItemButton>
      </ListItem>

      {submenuItems && (
        <Collapse in={subMenuOpen && open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              mt: 1,
              mb: 1,
              pl: 0,
            }}
          >
            {submenuItems.map((item, index) => {
              const isSubmenuActive = location.pathname === item.to;
              return (
                <Item
                  key={index}
                  title={item.title}
                  to={item.to}
                  icon={item.icon}
                  selected={isSubmenuActive ? item.title : selected}
                  setSelected={setSelected}
                  submenuItems={item.submenuItems}
                  open={open}
                  logout={item.logout}
                  isSubmenu={true}
                />
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
};

const openedMixin = (theme) => ({
  width: SidebarWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(12)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: SidebarWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  minHeight: 80,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
  position: 'relative',
  width: '100%',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.contrastText, 0.4)}, transparent)`,
  },
}));

const LogoBox = styled(Box)(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  opacity: open ? 1 : 0,
  transition: 'opacity 0.3s ease-in-out',
  '& .logo-text': {
    fontWeight: 700,
    fontSize: '1.75rem',
    letterSpacing: '0.5px',
    background: `linear-gradient(135deg, ${theme.palette.primary.contrastText} 0%, ${alpha(theme.palette.primary.contrastText, 0.8)} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: alpha(theme.palette.primary.contrastText, 0.15),
  padding: theme.spacing(1.25),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.contrastText, 0.25),
    transform: 'scale(1.1)',
    boxShadow: `0px 2px 8px ${alpha(theme.palette.primary.contrastText, 0.3)}`,
  },
  '& svg': {
    fontSize: '1.5rem',
  },
}));

const FooterBox = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  padding: theme.spacing(2, 0),
  borderTop: `2px solid ${alpha(theme.palette.divider, 0.6)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(10px)',
}));

export default function Sidebar({ handleLogout }) {
  const theme = useTheme();
  const [selected, setSelected] = useState("Dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Sync local state with global variable
  useEffect(() => {
    setSidebarOpen(isSideBarOpen);
  }, []);

  const handleSidebarOpen = () => {
    isSideBarOpen = true;
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    isSideBarOpen = false;
    setSidebarOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Drawer variant="permanent" open={isSideBarOpen}>
        <DrawerHeader>
          {sidebarOpen ? (
            <>
              <LogoBox open={sidebarOpen}>
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="GVP"
                    sx={{
                      height: 40,
                      width: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                {sidebarOpen && (
                  <Box
                    className="logo-text"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      letterSpacing: '0.5px',
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    GVP
                  </Box>
                )}
              </LogoBox>
              <StyledIconButton
                onClick={handleSidebarClose}
                size="small"
                aria-label="close drawer"
              >
                <ChevronLeftIcon />
              </StyledIconButton>
            </>
          ) : (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <StyledIconButton
                onClick={handleSidebarOpen}
                size="small"
                aria-label="open drawer"
              >
                <MenuIcon />
              </StyledIconButton>
            </Box>
          )}
        </DrawerHeader>
        <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5), borderWidth: 1 }} />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: theme.spacing(1, 0),
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.primary.main, 0.25),
              borderRadius: '4px',
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.4),
              },
            },
          }}
        >
          <List sx={{ py: 2, px: 0 }}>
            {menuItems.map((item, index) => (
              <Item
                key={index}
                title={item.title}
                to={item.to || "#"}
                icon={item.icon}
                selected={selected}
                setSelected={setSelected}
                open={sidebarOpen}
                submenuItems={item.submenuItems}
              />
            ))}
          </List>
        </Box>

        <FooterBox>
          <Item
            title="Αποσύνδεση"
            to="/logout"
            icon={<LogoutIcon />}
            selected={selected}
            setSelected={setSelected}
            open={sidebarOpen}
            logout={handleLogout}
          />
        </FooterBox>
      </Drawer>
    </Box>
  );
}
